import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface AgentSession {
  id: string
  goal: string
  status: string
  agent_mode: string
  files_modified: string[]
  patches_count: number
  duration_ms: number
  created_at: string
  completed_at: string
}

export function AgentActivityLog({ onClose }: { onClose: () => void }) {
  const [sessions, setSessions] = useState<AgentSession[]>([])
  const [skills, setSkills] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [tab, setTab] = useState<'sessions' | 'skills'>('sessions')

  useEffect(() => {
    Promise.all([
      authFetch(apiUrl('/api/agent-stream/sessions')).then(r => r.json()),
      authFetch(apiUrl('/api/agent-stream/skills')).then(r => r.json()),
    ]).then(([s, sk]) => {
      setSessions(Array.isArray(s) ? s : [])
      setSkills(Array.isArray(sk) ? sk : [])
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const handleRollback = async (sessionId: string) => {
    if (!confirm('Rollback all changes from this session?')) return
    const res = await authFetch(apiUrl('/api/agent-stream/rollback-session'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    })
    const data = await res.json()
    if (data.ok) {
      alert(`Rolled back: ${data.restored?.length || 0} files restored`)
      window.dispatchEvent(new CustomEvent('pulse:files-changed'))
    }
  }

  const handleReplay = async (skillId: string) => {
    const res = await authFetch(apiUrl(`/api/agent-stream/skills/${skillId}/replay`), { method: 'POST' })
    const data = await res.json()
    if (data.ok && data.goal) {
      window.dispatchEvent(new CustomEvent('pulse:agent-resume', { detail: { goal: data.goal } }))
      onClose()
    }
  }

  const handleDeleteSkill = async (skillId: string) => {
    await authFetch(apiUrl(`/api/agent-stream/skills/${skillId}`), { method: 'DELETE' })
    setSkills(prev => prev.filter(s => s.id !== skillId))
  }

  const formatTime = (ts: string) => {
    try {
      const d = new Date(ts)
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch { return ts }
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <span style={styles.title}>Agent Activity</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setTab('sessions')} style={{ ...styles.tabBtn, ...(tab === 'sessions' ? styles.tabActive : {}) }}>Sessions</button>
          <button onClick={() => setTab('skills')} style={{ ...styles.tabBtn, ...(tab === 'skills' ? styles.tabActive : {}) }}>Skills</button>
        </div>
        <button onClick={onClose} style={styles.closeBtn}>✕</button>
      </div>
      <div style={styles.body}>
        {loading && <div style={styles.empty}>Loading...</div>}

        {/* Skills tab */}
        {!loading && tab === 'skills' && (
          skills.length === 0 
            ? <div style={styles.empty}>No saved skills yet. Complete an agent task and click "Save as Skill".</div>
            : skills.map(sk => (
              <div key={sk.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={{ ...styles.statusDot, background: '#a78bfa' }} />
                  <span style={styles.goal}>{sk.name}</span>
                  <span style={styles.time}>used {sk.use_count}x</span>
                </div>
                <div style={styles.cardMeta}>
                  <span style={styles.duration}>{sk.goal?.slice(0, 50)}</span>
                </div>
                <div style={{ ...styles.actions, marginTop: 8 }}>
                  <button onClick={() => handleReplay(sk.id)} style={styles.resumeBtn}>▶ Replay</button>
                  <button onClick={() => handleDeleteSkill(sk.id)} style={styles.rollbackBtn}>✕ Delete</button>
                </div>
              </div>
            ))
        )}

        {/* Sessions tab */}
        {!loading && tab === 'sessions' && sessions.length === 0 && <div style={styles.empty}>No agent sessions yet</div>}
        {!loading && tab === 'sessions' && sessions.map(s => (
          <div key={s.id} style={styles.card} onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
            <div style={styles.cardHeader}>
              <span style={{ ...styles.statusDot, background: s.status === 'done' ? '#5fd4a4' : '#f07070' }} />
              <span style={styles.goal}>{s.goal}</span>
              <span style={styles.time}>{formatTime(s.created_at)}</span>
            </div>
            <div style={styles.cardMeta}>
              <span style={styles.badge}>{s.agent_mode || 'assisted'}</span>
              <span style={styles.duration}>{formatDuration(s.duration_ms)}</span>
              {s.files_modified?.length > 0 && (
                <span style={styles.files}>{s.files_modified.length} file(s)</span>
              )}
            </div>
            {expanded === s.id && (
              <div style={styles.details}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Goal:</span>
                  <span style={styles.detailValue}>{s.goal}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Mode:</span>
                  <span style={styles.detailValue}>{s.agent_mode || 'assisted'}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Duration:</span>
                  <span style={styles.detailValue}>{formatDuration(s.duration_ms)}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Patches:</span>
                  <span style={styles.detailValue}>{s.patches_count}</span>
                </div>
                {s.files_modified?.length > 0 && (
                  <div style={styles.fileList}>
                    <span style={styles.detailLabel}>Files modified:</span>
                    {s.files_modified.map(f => (
                      <div key={f} style={styles.fileItem}>
                        <span>📄 {f}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={styles.actions}>
                  <button onClick={(e) => { e.stopPropagation(); handleRollback(s.id) }} style={styles.rollbackBtn}>
                    ↩ Rollback All
                  </button>
                  {s.status === 'paused' && (
                    <button onClick={(e) => { e.stopPropagation(); handleResume(s.id, s.goal) }} style={styles.resumeBtn}>
                      ▶ Resume
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-0)' },
  header: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border)', gap: 8 },
  title: { font: '700 13px/1 system-ui', color: 'var(--text-0)', flex: 1 },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 14, padding: '2px 6px' },
  tabBtn: { padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600 },
  tabActive: { background: 'rgba(124,110,245,0.1)', borderColor: 'rgba(124,110,245,0.3)', color: 'var(--accent)' },
  body: { flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { color: 'var(--text-2)', fontSize: 12, textAlign: 'center', padding: 40 },
  card: { padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-1)', cursor: 'pointer', transition: 'border-color 0.2s' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 8 },
  statusDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  goal: { font: '11px/1.3 ui-monospace, monospace', color: 'var(--text-0)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  time: { font: '9px/1 ui-monospace, monospace', color: 'var(--text-2)', flexShrink: 0 },
  cardMeta: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 },
  badge: { font: '700 8px/1 system-ui', padding: '2px 6px', borderRadius: 4, background: 'rgba(124,110,245,0.1)', color: 'var(--accent)', textTransform: 'uppercase' as const },
  duration: { font: '9px/1 ui-monospace, monospace', color: 'var(--text-2)' },
  files: { font: '9px/1 ui-monospace, monospace', color: 'var(--accent-2)' },
  details: { marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' },
  fileList: { display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 8 },
  fileItem: { font: '10px/1.4 ui-monospace, monospace', color: 'var(--text-1)' },
  actions: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 },
  rollbackBtn: { padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(240,112,112,0.3)', background: 'rgba(240,112,112,0.08)', color: '#f07070', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-mono)' },
  resumeBtn: { padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(95,212,164,0.3)', background: 'rgba(95,212,164,0.08)', color: '#5fd4a4', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-mono)' },
  sessionId: { font: '8px/1 ui-monospace, monospace', color: 'var(--text-2)', opacity: 0.6 },
  detailRow: { display: 'flex', gap: 8, marginBottom: 4 },
  detailLabel: { font: '700 9px/1.4 system-ui', color: 'var(--text-2)', minWidth: 60 },
  detailValue: { font: '10px/1.4 ui-monospace, monospace', color: 'var(--text-1)' },
}
